import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
	Button,
	Card,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";

import { Breadcrumbs, PageCenteredProgress, RouteLayout } from "components";

import { useQuestBoxApi } from "api/questBox";
import useCatchError from "api/useCatchError";

import useQuery from "utils/useQuery";

import TierReward from "../../detailView/TierReward";
import RankIcon from "../List/RankIcon";

const ListItem = ({ ix, campaign, item, columns }) => {
	const color = (theme) => (campaign?.scope !== "OKX" && ix === 0 ? "#fff" : theme.palette.primary.main);

	return (
		<>
			{columns.some((x) => x === "rank") && (
				<TableCell
					sx={{
						boxShadow: "none",
						borderLeft: "1px solid #0F20E8",
						borderTop: "1px solid #0F20E8",
						borderBottom: "1px solid #0F20E8",
					}}
				>
					<div>
						<Grid container spacing={1}>
							<Grid item>
								<div style={{ width: "16px" }}>
									<RankIcon previousRank={item?.previousRank} rank={item?.rank} />
								</div>
							</Grid>
							<Grid item>
								<Typography
									sx={{
										fontSize: "18px",
										fontWeight: 700,
										width: "18px",
										color,
									}}
								>
									{campaign?.scope === "OKX" ? ix + 1 : item?.rank ?? "-"}
								</Typography>
							</Grid>
						</Grid>
					</div>
				</TableCell>
			)}
			{columns.some((x) => x === "name") && (
				<TableCell
					sx={{
						boxShadow: "none",
						borderTop: "1px solid #0F20E8",
						borderBottom: "1px solid #0F20E8",
					}}
				>
					{item?.email ? (
						<Grid container>
							{item?.tier !== null && item?.tier !== undefined && (
								<Grid item xs={1.5}>
									<img
										src={`https://cdn2.traderlands.com/general/dashboard-icons/ori_tier_${
											item.tier
										}${ix === 0 ? "_white" : ""}.svg`}
									/>
								</Grid>
							)}
							<Grid item>
								<Typography
									sx={{
										fontSize: "14px",
										color,
									}}
								>
									{item.email}
								</Typography>
							</Grid>
						</Grid>
					) : (
						<Typography color="white">-</Typography>
					)}
				</TableCell>
			)}
			{columns.some((x) => x === "average_profit") && (
				<TableCell
					sx={{
						boxShadow: "none",
						borderTop: "1px solid #0F20E8",
						borderBottom: "1px solid #0F20E8",
					}}
				>
					<Typography
						sx={{
							fontSize: "16px",
							color,
						}}
					>
						{item?.averageProfit ?? "0"}
					</Typography>
				</TableCell>
			)}
			{columns.some((x) => x === "volume") && (
				<TableCell
					sx={{
						boxShadow: "none",
						borderTop: "1px solid #0F20E8",
						borderBottom: "1px solid #0F20E8",
					}}
				>
					<Typography
						sx={{
							fontSize: "16px",
							color,
						}}
					>
						{campaign?.scope === "OKX"
							? `$${item?.volume}`
							: item?.totalVolume
							? `$${item?.totalVolume}`
							: "-"}
					</Typography>
				</TableCell>
			)}
			{columns.some((x) => x === "prize") && (
				<TableCell
					sx={{
						boxShadow: "none",
						...(campaign?.scope && { borderRight: "1px solid #0F20E8" }),
						borderTop: "1px solid #0F20E8",
						borderBottom: "1px solid #0F20E8",
					}}
				>
					<Typography
						sx={{
							color: (theme) => theme.palette.success.main,
							fontSize: "16px",
							fontWeight: 700,
						}}
					>
						{campaign?.scope === "OKX" && item?.reward}
						{campaign?.scope !== "OKX" && item?.tier !== null && item?.tier !== undefined
							? campaign.leaderboardInfo.tierList?.[item.tier]?.reward
							: "0"}
					</Typography>
				</TableCell>
			)}
			{columns.some((x) => x === "score") && (
				<TableCell
					sx={{
						boxShadow: "none",
						borderRight: "1px solid #0F20E8",
						borderTop: "1px solid #0F20E8",
						borderBottom: "1px solid #0F20E8",
					}}
				>
					<Typography sx={{ fontSize: "16px", fontWeight: 700, color }}>{item?.score ?? "0"}</Typography>
				</TableCell>
			)}
		</>
	);
};

export default function FullList() {
	const [processing, setProcessing] = useState(false);
	const [loadMoreDisabled, setLoadMoreDisabled] = useState(false);
	const [campaign, setCampaign] = useState();
	const [selfRank, setSelfRank] = useState();
	const [leaderboard, setLeaderboard] = useState([]);
	const [pageNumber, setPageNumber] = useState(0);

	const PAGE_SIZE = 5;

	const { id } = useParams();
	const navigate = useNavigate();
	const { t, i18n } = useTranslation("questBox");
	const query = useQuery();

	const { getCampaign, getSelfLeaderboard, getLeaderboard } = useQuestBoxApi();
	const catchError = useCatchError();

	const originalCampaignId = query.get("campaignId");

	const handleLoadMore = () => {
		setProcessing(true);

		getLeaderboard({ campaignId: id, pageNumber: pageNumber + 1, pageSize: PAGE_SIZE })
			.then((data) => {
				if (!data?.data?.leaderboard?.length) {
					setLoadMoreDisabled(true);
					return;
				}

				setLeaderboard([...leaderboard, ...data?.data?.leaderboard]);
				setPageNumber(pageNumber + 1);
			})
			.catch(catchError)
			.finally(() => setProcessing(false));
	};

	useEffect(() => {
		getCampaign(id)
			.then((res) => setCampaign(res.data.campaign))
			.catch(catchError);
	}, []);

	useEffect(() => {
		if (campaign?.leaderboardInfo) {
			const pageNumber = query.get("pageNumber") ?? 0;

			Promise.all([
				getSelfLeaderboard(id).then((data) => {
					setSelfRank(data?.data?.userRank);
				}),
				getLeaderboard({ campaignId: id, pageNumber, pageSize: PAGE_SIZE }).then((data) => {
					if (!data?.data?.leaderboard?.length) {
						return;
					}

					setLeaderboard(data?.data?.leaderboard);
				}),
			]).catch(catchError);
		}
	}, [campaign?.leaderboardInfo]);

	return (
		<>
			{!campaign || !leaderboard?.length ? (
				<PageCenteredProgress />
			) : (
				<RouteLayout
					headerComp={
						<Breadcrumbs
							paths={[
								{
									text: "Quest Box",
									onClick: () => navigate("/quest-box"),
								},
								{
									text: campaign?.title?.en,
									onClick: () => {
										originalCampaignId
											? navigate(`/quest-box/${originalCampaignId}/details`)
											: navigate(`/quest-box/${id}/details`);
									},
								},
								{
									text: t("questbox_full_list_breadcrumbs_title"),
								},
							]}
						/>
					}
				>
					<Card sx={{ p: 3, mt: 2 }}>
						<Grid container justifyContent="center" alignItems="center" sx={{ ml: "auto", mr: "auto" }}>
							<Grid item xs={12}>
								<Typography sx={{ fontSize: "24px", fontWeight: 700, textAlign: "center", mb: 1 }}>
									{t("quest_box_leaderboard_details_page_title")}
								</Typography>

								<Typography sx={{ textAlign: "center" }}>
									{campaign?.leaderboardInfo?.explanationText?.[i18n?.resolvedLanguage]}
								</Typography>
							</Grid>
							{campaign && campaign?.layout?.web?.rewardTiers && (
								<Grid item xs={12} mt={1} mb={1}>
									<TierReward campaign={campaign} />
								</Grid>
							)}
							<Grid item xs={12}>
								<TableContainer>
									<Table
										sx={{ minWidth: 750, borderCollapse: "separate", borderSpacing: "0 0.75rem" }}
									>
										<TableHead sx={{ borderRadius: 4, border: "none", backgroundColor: "#F0F0F5" }}>
											<TableRow>
												{(campaign?.leaderboardMapping?.columns ?? []).map((title, ix) => (
													<TableCell key={ix}>{t(`questbox_table_head_${title}`)}</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{[...(campaign?.scope !== "OKX" ? [selfRank] : []), ...leaderboard].map(
												(item, ix) => (
													<TableRow
														{...(ix === 0 && campaign?.scope !== "OKX"
															? {
																	sx: {
																		backgroundColor: (theme) =>
																			theme.palette.primary.main,
																	},
															  }
															: {
																	sx: {},
															  })}
													>
														<ListItem
															ix={ix}
															campaign={campaign}
															item={item}
															columns={campaign?.leaderboardMapping?.columns}
														/>
													</TableRow>
												)
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Grid>
							<Grid item xs={12}>
								<Button
									disabled={processing || loadMoreDisabled}
									onClick={() => handleLoadMore()}
									variant="outlined"
									fullWidth
								>
									{t("quest_box_leaderboard_load_more")}
								</Button>
							</Grid>
						</Grid>
					</Card>
				</RouteLayout>
			)}
		</>
	);
}
